import React, { useState, useEffect } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import Constant from "../Constant";

const CreatePlanModal = ({
  open,
  onClose,
  onPlanCreated,
  showNotification,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    duration: "",
    description: "",
    isActive: true,
  });
  const [features, setFeatures] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchFeatures();
    }
  }, [open]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Constant.apiBasePath}features/active`
      );
      setFeatures(
        response.data.map((feature) => ({
          feature: feature._id,
          name: feature.name,
          enabled: false,
        }))
      );
    } catch (error) {
      showNotification("Failed to load features", "error");
      console.error("Error loading features:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFeatureChange = (featureId, checked) => {
    setFeatures(
      features.map((feature) =>
        feature.feature === featureId
          ? { ...feature, enabled: checked }
          : feature
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // First create the plan with basic information
      const planResponse = await axios.post(
        `${Constant.apiBasePath}plans/create`,
        {
          ...formData,
          planFeatures: features
            .filter((f) => f.enabled)
            .map((f) => ({
              feature: f.feature,
              name: f.name,
              enabled: true,
            })),
        }
      );

      // Get the newly created plan ID from the response
      const newPlanId = planResponse.data.plan._id;
      console.log("New plan:", planResponse);

      // Only now update individual feature settings
      const enabledFeatures = features.filter((f) => f.enabled);
      for (const feature of enabledFeatures) {
        await axios.put(
          `${Constant.apiBasePath}plans/${newPlanId}/features/${feature.feature}`,
          { enabled: true }
        );
      }

      showNotification("Plan created successfully");
      onPlanCreated();
      onClose();
    } catch (error) {
      if (error.response && error.response.data) {
        showNotification(error.response.data.message, "error");
      } else {
        showNotification("Failed to create plan", "error");
      }
      console.error("Error creating plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalStyle = {
    formGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    },
    formGroup: {
      marginBottom: "16px",
    },
    input: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
    },
    textarea: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      gridColumn: "span 2",
    },
    label: {
      display: "block",
      marginBottom: "6px",
      fontWeight: "500",
    },
    checkboxGroup: {
      gridColumn: "span 2",
      marginBottom: "16px",
    },
    checkboxOptions: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "10px",
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
    },
    buttonGroup: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
      gridColumn: "span 2",
      marginTop: "16px",
    },
    button: {
      padding: "8px 16px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    submitButton: {
      backgroundColor: "#4a90e2",
      color: "white",
      border: "none",
    },
    cancelButton: {
      backgroundColor: "#f5f5f5",
      border: "1px solid #ddd",
    },
    modalContent: {
      padding: "20px",
      maxWidth: "600px",
      maxHeight: "80vh",
      overflow: "auto",
    },
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      contentStyle={{ maxWidth: "600px", width: "80%" }}
      modal
      nested
    >
      <style>{`
        .plan-modal-content { padding: 20px; max-width: 600px; max-height: 80vh; overflow: auto; font-size: 1rem; }
        .plan-modal-title { margin-bottom: 20px; text-align: center; font-size: 1.5rem; }
        .plan-modal-label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 1rem; }
        .plan-modal-input, .plan-modal-textarea { width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 1rem; }
        .plan-modal-button { padding: 8px 16px; border-radius: 4px; cursor: pointer; font-size: 1rem; }
        @media (max-width: 600px) {
          .plan-modal-content { padding: 8px; font-size: 0.95rem; }
          .plan-modal-title { font-size: 1.1rem; }
          .plan-modal-label { font-size: 0.95rem; }
          .plan-modal-input, .plan-modal-textarea { font-size: 0.95rem; padding: 6px; }
          .plan-modal-button { font-size: 0.95rem; padding: 6px 10px; }
        }
      `}</style>
      <div className="plan-modal-content">
        <h2 className="plan-modal-title">Create New Plan</h2>
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading features...</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={modalStyle.formGrid}>
              <div style={modalStyle.formGroup}>
                <label className="plan-modal-label">
                  Plan Name:
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="plan-modal-input"
                  />
                </label>
              </div>

              <div style={modalStyle.formGroup}>
                <label className="plan-modal-label">
                  Amount (₹):
                  <input
                    type="number"
                    step="1"
                    name="amount"
                    value={formData.amount}
                    onChange={handleChange}
                    required
                    className="plan-modal-input"
                  />
                </label>
              </div>

              <div style={modalStyle.formGroup}>
                <label className="plan-modal-label">
                  Duration (Months):
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    required
                    className="plan-modal-input"
                  />
                </label>
              </div>

              <div style={{ ...modalStyle.formGroup, gridColumn: "span 2" }}>
                <label className="plan-modal-label">
                  Description:
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="plan-modal-textarea"
                  />
                </label>
              </div>

              <div style={modalStyle.checkboxGroup}>
                <label className="plan-modal-label">Features:</label>
                <div style={modalStyle.checkboxOptions}>
                  {features.map((feature) => (
                    <label
                      key={feature.feature}
                      style={modalStyle.checkboxLabel}
                      className="plan-modal-label"
                    >
                      <input
                        type="checkbox"
                        checked={feature.enabled}
                        onChange={(e) =>
                          handleFeatureChange(feature.feature, e.target.checked)
                        }
                        style={{ marginRight: "8px" }}
                      />
                      {feature.name}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ ...modalStyle.formGroup, gridColumn: "span 2" }}>
                <label
                  style={{ ...modalStyle.checkboxLabel, marginBottom: "0" }}
                  className="plan-modal-label"
                >
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    style={{ marginRight: "8px" }}
                  />
                  Active
                </label>
              </div>

              <div style={modalStyle.buttonGroup}>
                <button
                  type="button"
                  onClick={onClose}
                  className="plan-modal-button"
                  style={modalStyle.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="plan-modal-button"
                  style={modalStyle.submitButton}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </Popup>
  );
};

export default CreatePlanModal;
